import type { User, QuotaInfo, Server, Prediction, ChannelVisit } from '../types';
import { isDiscordActivity } from './discord-sdk';

interface APIResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

// Use relative /api path in Discord Activity (proxied via URL mappings)
// Use full URL otherwise - default to production API if env var not set
const API_BASE = isDiscordActivity()
  ? '/api'
  : (import.meta.env.VITE_API_URL || 'https://foretell-api.semicolon.workers.dev/api');

async function fetchAPI<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<APIResponse<T>> {
  const token = localStorage.getItem('auth_token');

  try {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      return { success: false, error: data.error || 'Request failed' };
    }

    return { success: true, data };
  } catch (error) {
    console.error('API error:', error);
    return { success: false, error: 'Network error' };
  }
}

export const api = {
  auth: {
    exchangeCode: (code: string) =>
      fetchAPI<{ token: string; user: User }>('/auth/discord/callback', {
        method: 'POST',
        body: JSON.stringify({ code }),
      }),

    refresh: () =>
      fetchAPI<{ token: string }>('/auth/refresh', { method: 'POST' }),

    logout: () =>
      fetchAPI('/auth/logout', { method: 'POST' }),

    getMe: () =>
      fetchAPI<User>('/auth/me'),
  },

  predictions: {
    list: (serverId: string, channelId?: string) =>
      fetchAPI<Prediction[]>(`/predictions?serverId=${serverId}${channelId ? `&channelId=${channelId}` : ''}`),

    create: (data: {
      text: string;
      confidence: number;
      deadline: string | null;
      invalidationThesis?: string;
      isAnonymous?: boolean;
      channelId: string;
      serverId: string;
    }) =>
      fetchAPI<{ id: string }>('/predictions', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    vote: (predictionId: string, voteType: 'success' | 'fail' | 'invalid') =>
      fetchAPI(`/predictions/${predictionId}/vote`, {
        method: 'POST',
        body: JSON.stringify({ voteType }),
      }),

    resolve: (predictionId: string, status: 'success' | 'fail' | 'invalidated') =>
      fetchAPI(`/predictions/${predictionId}/resolve`, {
        method: 'POST',
        body: JSON.stringify({ status }),
      }),

    delete: (predictionId: string) =>
      fetchAPI(`/predictions/${predictionId}`, { method: 'DELETE' }),

    getMine: () =>
      fetchAPI<Prediction[]>('/predictions/mine'),
  },

  entitlements: {
    check: () =>
      fetchAPI<{ hasAccess: boolean }>('/entitlements/check'),

    getServerAccess: (serverId: string) =>
      fetchAPI<{ hasAccess: boolean; subscription: Server['subscription'] }>(
        `/entitlements/server/${serverId}`
      ),
  },

  quota: {
    get: () =>
      fetchAPI<QuotaInfo>('/quota'),

    increment: () =>
      fetchAPI<QuotaInfo>('/quota/increment', { method: 'POST' }),
  },

  servers: {
    list: () =>
      fetchAPI<Server[]>('/servers'),

    get: (serverId: string) =>
      fetchAPI<Server>(`/servers/${serverId}`),

    updateSettings: (serverId: string, settings: Partial<Server['settings']>) =>
      fetchAPI<Server>(`/servers/${serverId}/settings`, {
        method: 'PATCH',
        body: JSON.stringify(settings),
      }),
  },

  subscriptions: {
    createCheckout: (serverId: string, plan: 'monthly' | 'yearly') =>
      fetchAPI<{ url: string }>('/subscriptions/checkout', {
        method: 'POST',
        body: JSON.stringify({ serverId, plan }),
      }),

    getPortal: (serverId: string) =>
      fetchAPI<{ url: string }>(`/subscriptions/portal/${serverId}`),

    cancel: (serverId: string) =>
      fetchAPI('/subscriptions/cancel', {
        method: 'POST',
        body: JSON.stringify({ serverId }),
      }),
  },

  analytics: {
    getServer: (serverId: string) =>
      fetchAPI<{
        totalPredictions: number;
        successRate: number;
        averageConfidence: number;
        predictionsByMonth: Array<{ month: string; count: number }>;
      }>(`/analytics/server/${serverId}`),

    getUser: () =>
      fetchAPI<{
        totalPredictions: number;
        successRate: number;
        accuracyByConfidence: Array<{ confidence: number; accuracy: number }>;
      }>('/analytics/user'),
  },

  channelVisits: {
    register: (data: {
      channelId: string;
      channelType: number;
      channelName?: string;
      guildId?: string;
      guildName?: string;
      guildIcon?: string;
      recipientId?: string;
      recipientName?: string;
      recipientAvatar?: string;
      groupIcon?: string;
      groupOwnerId?: string;
    }) =>
      fetchAPI<{ success: boolean }>('/channel-visits', {
        method: 'POST',
        body: JSON.stringify(data),
      }),

    list: () => fetchAPI<ChannelVisit[]>('/channel-visits'),
  },
};

export function getDiscordOAuthUrl(state?: string) {
  const clientId = import.meta.env.VITE_DISCORD_CLIENT_ID;
  const redirectUri = encodeURIComponent(
    `${window.location.origin}/auth/callback`
  );
  const scope = encodeURIComponent('identify email guilds');
  const stateParam = state ? `&state=${encodeURIComponent(state)}` : '';

  return `https://discord.com/api/oauth2/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=${scope}${stateParam}`;
}

export function getDiscordAppSubscriptionUrl() {
  const clientId = import.meta.env.VITE_DISCORD_CLIENT_ID;
  return `https://discord.com/application-directory/${clientId}/store`;
}
